import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, allMovies, movieCategories } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const movie = useMemo(() => {
    return getMovieById(id || '');
  }, [id]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return allMovies
      .filter(m => m.id !== movie.id && m.category === movie.category)
      .slice(0, 6);
  }, [movie]);

  const moreMovies = useMemo(() => {
    return allMovies.filter(m => movie && m.id !== movie.id).slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 text-center py-32">
          <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-4 block"></i>
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">电影未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，该电影信息不存在或已被移除</p>
          <Link to="/" className="inline-flex items-center px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: movieCategories.find(c => c.slug === movie.category)?.name || '电影', href: `/category/${movie.category}` },
    { label: movie.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    alternateName: movie.titleEn,
    description: movie.description,
    dateCreated: `${movie.year}-01-01`,
    director: { '@type': 'Person', name: movie.director },
    actor: movie.cast?.map(name => ({ '@type': 'Person', name })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
    },
    image: movie.image,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://beiwocinema.com/' },
      { '@type': 'ListItem', position: 2, name: '电影', item: `https://beiwocinema.com/category/${movie.category}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `《${movie.title}》可以在被窝影院免费观看吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `是的，《${movie.title}》在被窝影院完全免费在线观看，无需注册，无广告打扰，高清流畅不缓冲。`,
        },
      },
      {
        '@type': 'Question',
        name: `《${movie.title}》是什么类型的电影？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${movie.title}》是一部${movie.tags.join('、')}类型的电影，由${movie.director}执导，${movie.cast?.slice(0, 2).join('、')}等主演，${movie.year}年上映。`,
        },
      },
      {
        '@type': 'Question',
        name: '被窝影院有哪些热门电影推荐？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '被窝影院收录了星际穿越、肖申克的救赎、千与千寻、盗梦空间、泰坦尼克号等众多高分经典电影，涵盖动作、喜剧、科幻、爱情、动画等多种类型，满足各类影迷需求。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            {/* Article Hero */}
            <article className="w-full px-4 md:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
                {/* Left - Poster */}
                <div className="lg:col-span-4">
                  <div className="relative rounded-xl overflow-hidden aspect-[3/4] bg-background-200">
                    <img
                      src={movie.image}
                      alt={movie.title}
                      className="w-full h-full object-cover object-top"
                      title={`${movie.title} - 被窝影院免费在线观看`}
                    />
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
                      <i className="ri-star-fill text-accent-400"></i>
                      <span className="text-white font-bold">{movie.rating}</span>
                    </div>
                  </div>
                </div>

                {/* Right - Info */}
                <div className="lg:col-span-8">
                  <div className="flex flex-col h-full justify-center">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-1">
                      {movie.title}
                    </h1>
                    <p className="text-sm text-foreground-500 mb-6">{movie.titleEn}</p>

                    <div className="flex flex-wrap items-center gap-3 mb-6">
                      {movie.tags.map(tag => (
                        <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-700 text-sm font-medium rounded-full whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                      {movie.isHot && (
                        <span className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full whitespace-nowrap">
                          热门推荐
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">上映年份</p>
                        <p className="text-sm font-semibold text-foreground-900">{movie.year}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">时长</p>
                        <p className="text-sm font-semibold text-foreground-900">{movie.duration}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">评分</p>
                        <p className="text-sm font-semibold text-accent-600">{movie.rating} / 10</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">导演</p>
                        <p className="text-sm font-semibold text-foreground-900">{movie.director}</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-xs text-foreground-500 mb-1">主演</p>
                      <p className="text-sm text-foreground-800">{movie.cast?.join(' / ')}</p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <a
                        href="#"
                        className="inline-flex items-center px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-play-circle-line mr-2 text-lg"></i>立即播放
                      </a>
                      <button className="inline-flex items-center px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 border border-background-300 rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer">
                        <i className="ri-heart-line mr-2"></i>收藏
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mt-10 md:mt-12">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-4">剧情简介</h2>
                <div className="prose prose-sm max-w-none">
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {movie.description}
                  </p>
                </div>
              </div>

              {/* Related Movies */}
              {relatedMovies.length > 0 && (
                <div className="mt-12 md:mt-16">
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-6">
                    同类推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                    {relatedMovies.map((m) => (
                      <MovieCard key={m.id} {...m} />
                    ))}
                  </div>
                </div>
              )}

              {/* More Movies */}
              {moreMovies.length > 0 && (
                <div className="mt-12 md:mt-16 mb-16">
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-6">
                    猜你喜欢
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                    {moreMovies.map((m) => (
                      <MovieCard key={m.id} {...m} />
                    ))}
                  </div>
                </div>
              )}
            </article>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

